import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Bell,
  BookmarkSimple,
  Trash,
  Check,
  Checks,
  Package,
  Gift,
  Star as StarIcon,
  Clock,
  WarningCircle,
  BellSlash
} from '@phosphor-icons/react';
import { FaStar } from 'react-icons/fa';
import { useNotifications } from '../../context/NotificationsContext';
import { useFavorites } from '../../context/FavoritesContext';

// Configuración de íconos por tipo de notificación
const notificationConfig = {
  order: { icon: Package, bgColor: 'bg-blue-50', iconBg: 'bg-blue-500' },
  promo: { icon: Gift, bgColor: 'bg-primary-100', iconBg: 'bg-brand-primary' },
  success: { icon: Check, bgColor: 'bg-emerald-50', iconBg: 'bg-emerald-500' },
  review: { icon: StarIcon, bgColor: 'bg-tertiary-100', iconBg: 'bg-tertiary-1000' },
  reminder: { icon: Clock, bgColor: 'bg-purple-50', iconBg: 'bg-purple-500' },
  alert: { icon: WarningCircle, bgColor: 'bg-red-50', iconBg: 'bg-red-500' },
  default: { icon: Bell, bgColor: 'bg-gray-50', iconBg: 'bg-gray-500' },
};

// Formatear tiempo relativo
const formatTimeAgo = (date) => {
  const now = new Date();
  const notifDate = new Date(date);
  const diffMs = now - notifDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `${diffDays}d`;
  return notifDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

const SideMenu = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('notifications');
  const {
    notifications,
    groupedNotifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    pushSupported,
    pushPermission,
    requestPushPermission,
    loading: notifLoading
  } = useNotifications();
  const { favorites, removeFavorite, loading: favLoading } = useFavorites();

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[200]"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-[201] flex flex-col rounded-l-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {activeTab === 'notifications' ? 'Notificaciones' : 'Guardados'}
              </h2>
              <div className="flex items-center gap-1">
                {activeTab === 'notifications' && unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    title="Marcar todas como leídas"
                  >
                    <Checks size={20} className="text-gray-500" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'notifications'
                    ? 'text-brand-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Bell size={18} />
                <span>Notificaciones</span>
                {unreadCount > 0 && (
                  <span className="w-5 h-5 bg-primary-1000 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {activeTab === 'notifications' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-1000"
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'favorites'
                    ? 'text-brand-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BookmarkSimple size={18} />
                <span>Guardados</span>
                {favorites.length > 0 && (
                  <span className="w-5 h-5 bg-gray-200 text-gray-600 text-xs font-bold rounded-full flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
                {activeTab === 'favorites' && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-1000"
                  />
                )}
              </button>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'notifications' ? (
                <div className="p-4">
                  {/* Banner de permisos push */}
                  {pushSupported && pushPermission !== 'granted' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mb-4 p-4 rounded-2xl ${
                        pushPermission === 'denied'
                          ? 'bg-gray-100'
                          : 'bg-gradient-to-r from-primary-500 to-primary-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          pushPermission === 'denied' ? 'bg-gray-300' : 'bg-white/20'
                        }`}>
                          {pushPermission === 'denied'
                            ? <BellSlash size={20} className="text-gray-500" />
                            : <Bell size={20} className="text-white" />
                          }
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${
                            pushPermission === 'denied' ? 'text-gray-700' : 'text-white'
                          }`}>
                            {pushPermission === 'denied'
                              ? 'Notificaciones bloqueadas'
                              : 'Activa las notificaciones'
                            }
                          </p>
                          <p className={`text-xs ${
                            pushPermission === 'denied' ? 'text-gray-500' : 'text-primary-100'
                          }`}>
                            {pushPermission === 'denied'
                              ? 'Actívalas en configuración del navegador'
                              : 'No te pierdas tus pedidos y ofertas'
                            }
                          </p>
                        </div>
                        {pushPermission !== 'denied' && (
                          <button
                            onClick={requestPushPermission}
                            className="px-3 py-1.5 bg-white text-brand-primary text-sm font-semibold rounded-xl"
                          >
                            Activar
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Lista de notificaciones */}
                  {notifLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-52 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Bell size={28} className="text-gray-300" />
                      </div>
                      <h3 className="text-gray-900 font-semibold mb-1">Sin notificaciones</h3>
                      <p className="text-gray-500 text-sm px-4">
                        Aquí aparecerán las actualizaciones de tus pedidos
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(groupedNotifications).map(([date, items]) => (
                        <div key={date}>
                          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                            {date}
                          </h3>
                          <div className="space-y-2">
                            {items.map((notification) => {
                              const config = notificationConfig[notification.type] || notificationConfig.default;
                              const IconComponent = config.icon;
                              const isRead = notification.read;

                              return (
                                <motion.div
                                  key={notification._id}
                                  layout
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, x: -100 }}
                                  className={`relative flex items-start gap-3 p-3 rounded-xl transition-all ${
                                    isRead ? 'bg-white' : config.bgColor
                                  }`}
                                >
                                  {!isRead && (
                                    <div className="absolute top-3 left-1 w-1.5 h-1.5 bg-primary-1000 rounded-full" />
                                  )}

                                  <div className={`flex-shrink-0 w-9 h-9 rounded-xl ${config.iconBg} flex items-center justify-center`}>
                                    <IconComponent size={18} className="text-white" />
                                  </div>

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <h4 className={`text-sm font-semibold line-clamp-1 ${isRead ? 'text-gray-600' : 'text-gray-900'}`}>
                                        {notification.title}
                                      </h4>
                                      <span className="text-xs text-gray-400 whitespace-nowrap">
                                        {formatTimeAgo(notification.createdAt)}
                                      </span>
                                    </div>
                                    <p className={`text-xs mt-0.5 line-clamp-2 ${isRead ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {notification.message}
                                    </p>

                                    <div className="flex items-center gap-3 mt-2">
                                      {!isRead && (
                                        <button
                                          onClick={() => markAsRead(notification._id)}
                                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                          <Check size={12} />
                                          <span>Leída</span>
                                        </button>
                                      )}
                                      <button
                                        onClick={() => deleteNotification(notification._id)}
                                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 ml-auto"
                                      >
                                        <Trash size={12} />
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4">
                  {/* Guardados */}
                  {favLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : favorites.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-52 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <BookmarkSimple size={28} className="text-gray-300" />
                      </div>
                      <h3 className="text-gray-900 font-semibold mb-1">Sin favoritos</h3>
                      <p className="text-gray-500 text-sm px-4">
                        Guarda tus negocios favoritos para acceder rápidamente
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {favorites.map((business) => (
                        <motion.div
                          key={business._id || business.id}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -100 }}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                        >
                          <img
                            src={business.logo || '/placeholder.svg'}
                            alt={business.name}
                            className="w-12 h-12 rounded-xl object-cover"
                            loading="lazy"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.svg'; }}
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                              {business.name}
                            </h4>
                            <div className="flex items-center gap-1 mt-0.5">
                              <FaStar size={12} className="text-rating-star" />
                              <span className="text-xs text-gray-500">
                                {business.rating?.toFixed(1) || '4.5'}
                              </span>
                              {business.category?.name && (
                                <>
                                  <span className="text-gray-300 mx-1">·</span>
                                  <span className="text-xs text-gray-500">{business.category.name}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeFavorite(business._id || business.id)}
                            className="p-2 hover:bg-gray-200 rounded-xl transition-colors"
                          >
                            <BookmarkSimple size={18} className="text-rating-star" weight="fill" />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SideMenu;
